import { Validators } from '@angular/forms';
import { InputConverter } from '../../common/helpers/inputConverter';
import { regex } from '../../common/regex.constants';
import { TranslationService } from '../../common/services/languages/translation.service';

/**
  * @class Contains the properties available of the Customer information
  */
export class Customer {
  public formBindings: object;
  public customerType: string;
  public firstName: string;
  public guid: string;
  public objectId: string;
  public lastName: string;
  public preferredLanguage: string;
  public createSource: string;
  public lastUpdateSource: string;
  public lastUpdateDate: string;
  public createDate: string;
  public phoneNumber: string;
  public emails: [Email];
  public uiLanguage: string;
  public address: string;
  public city: string;
  public state: string;
  public zip: string;
  public country: string;
  public signupType: string;
  public smsConsent: string;

  constructor(public data: object) { }

  /**
   * @function __defaults Return the defaults values for the model
   * @returns All the values in the CustomerModel interface as ''
   **/
  public __defaults(): object {
    return this.formBindings = {
      customerType: '',
      firstName: '',
      guid: '',
      objectId: '',
      lastName: '',
      preferredLanguage: '',
      createSource: '',
      lastUpdateSource: '',
      lastUpdateDate: '',
      createDate: '',
      phoneNumber: '',
      email: '',
      uiLanguage: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      signupType: '',
      smsConsent: ''
    };
  }

  /**
   * @function __formBindings Returns the values for initialize in case of read mode
   * @returns CustomerModel
   **/
  public __formBindings(bindings: object): object {
    return {
      'customerType': bindings['customerType'] ? bindings['customerType'] : '',
      'firstName': bindings['firstName'] ? bindings['firstName'] : '',
      'guid': bindings['guid'] ? bindings['guid'] : '',
      'objectId': bindings['objectId'] ? bindings['objectId'] : '',
      'lastName': bindings['lastName'] ? bindings['lastName'] : '',
      'preferredLanguage': bindings['preferredLanguage'] ? bindings['preferredLanguage'] : '',
      'createSource': bindings['createSource'] ? bindings['createSource'] : '',
      'lastUpdateSource': bindings['lastUpdateSource'] ? bindings['lastUpdateSource'] : '',
      'lastUpdateDate': bindings['lastUpdateDate'] ? bindings['lastUpdateDate'] : '',
      'createDate': bindings['createDate'] ? bindings['createDate'] : '',
      'phoneNumber': bindings['phoneNumbers'] ? bindings['phoneNumbers'][0]['phoneNumber'] : '',
      'email': bindings['emails'] ? bindings['emails'][0]['emailAddress'] : '',
      'uiLanguage': bindings['uiLanguage'] ? bindings['uiLanguage'] : '',
      'address': bindings['addresses'] ? bindings['addresses'][0]['address'] : '',
      'city': bindings['addresses'] ? bindings['addresses'][0]['city'] : '',
      'state': bindings['addresses'] ? bindings['addresses'][0]['state'] : '',
      'zip': bindings['addresses'] ? bindings['addresses'][0]['zipCode'] : '',
      'country': bindings['addresses'] ? bindings['addresses'][0]['country'] : '',
      'signupType': bindings['signupType'] ? this.getSignupType(bindings['signupType']) : '',
      'smsConsent': bindings['smsConsent'] ? bindings['smsConsent'] : '',
    };
  }

  /**
   * @function values Return the values send in the data property
   * @returns SignUpModel Interface
   **/
  public get values(): object {
    return this.data !== undefined ? this.__formBindings({ ...this.data }) : this.__defaults();
  }

  /**
   * @function form Return the form controls to be used in the customer form
   * @returns Object with the controls
   **/
  public get form(): object {
    return this.formControls;
  }

  /**
   * @function formControls Controls for the dealer view
   * @returns Object with the required controls
   **/
  public get formControls(): object {
    return {
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      email: this.emailControl,
      phoneNumber: this.phoneControl,
      customerType: this.customerTypeControl,
      guid: this.guidControl,
      objectId: this.objectIdControl,
      preferredLanguage: this.preferredLanguageControl,
      createSource: this.createSourceControl,
      lastUpdateSource: this.lastUpdateSourceControl,
      lastUpdateDate: this.lastUpdateDateControl,
      createDate: this.createDateControl,
      uiLanguage: this.uiLanguageControl,
      address: this.addressControl,
      city: this.cityControl,
      state: this.stateControl,
      zip: this.zipControl,
      country: this.countryControl,
      signupType: this.signupTypeControl,
      smsConsent: this.smsConsentControl
    };
  }

  /**
 * @function firstNameControl Controls for First Name field
 * @returns Array of validators
 **/
  public get firstNameControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.pattern(regex.precedingAndTrailingSpaces),
      Validators.pattern(regex.wordCount),
      Validators.maxLength(64),
      Validators.pattern(regex.firstNamePattern)
      ]];
  }

  /**
  * @function lastNameControl Controls for Last Name field
  * @returns Array of validators
  **/
  public get lastNameControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces),
      Validators.maxLength(64),
      Validators.pattern(regex.lastNamePattern)
      ]];
  }

  /**
  * @function emailControl Controls for email field
  * @returns Array of validators
  **/
  public get emailControl(): any[] {
    return [
      { value: '', disabled: true },
      [Validators.required,
      Validators.minLength(1),
      Validators.maxLength(64),
      Validators.pattern(regex.emailPattern)]
    ];
  }

  /**
   * @function phoneControlCustomer Controls for phone field when customer and dealer
   * @returns Array of validators
   **/
  public get phoneControl(): any[] {
    return [
      { value: '', disabled: true },
      [Validators.required,
      Validators.minLength(10),
      Validators.maxLength(14),
      Validators.pattern(regex.phoneNumberPattern)]];
  }

  /**
   * @function customerTypeControl Controls for customer type field
   * @returns Array of validators
   **/
  public get customerTypeControl(): any[] {
    return [
      { value: '', disabled: true },
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function guidControl Controls for guid field
   * @returns Array of validators
   **/
  public get guidControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function objectIdControl Controls for object id field
   * @returns Array of validators
   **/
  public get objectIdControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function preferredLanguageControl Controls for preffered language field
   * @returns Array of validators
   **/
  public get preferredLanguageControl(): any[] {
    return [
      { value: '', disabled: false },
      [Validators.required]
    ];
  }

  /**
   * @function createSourceControl Controls for create source field
   * @returns Array of validators
   **/
  public get createSourceControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function lastUpdateSourceControl Controls for last update source field
   * @returns Array of validators
   **/
  public get lastUpdateSourceControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function lastUpdateDateControl Controls for Last update date field
   * @returns Array of validators
   **/
  public get lastUpdateDateControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function createDateControl Controls for create date field
   * @returns Array of validators
   **/
  public get createDateControl(): any[] {
    return [
      '',
      [Validators.required,
      Validators.maxLength(64),
      Validators.pattern(regex.wordCount),
      Validators.pattern(regex.precedingAndTrailingSpaces)]];
  }

  /**
   * @function uiLanguageControl Controls for ui language field
   * @returns Array of validators
   **/
  public get uiLanguageControl(): any[] {
    return [
      { value: '', disabled: true },
      [Validators.required]
    ];
  }

  /**
   * @function addressControl Controls for address field
   * @returns Array of validators
   **/
  public get addressControl(): any[] {
    return [
      '',
      []];
  }

  /**
   * @function smsConsentControl Controls for SMS consent field
   * @returns Array of validators
   **/
  public get smsConsentControl(): any[] {
    return [
      { value: '', disabled: true },
      [Validators.required]
    ];
  }

  /**
    * @function cityControl Controls for city field
    * @returns Array of validators
    **/
  public get cityControl(): any[] {
    return [
      '',
      []];
  }

  /**
   * @function stateControl Controls for state field
   * @returns Array of validators
   **/
  public get stateControl(): any[] {
    return [
      { value: '', disabled: true },
      []];
  }

  /**
   * @function zipControl Controls for zip field
   * @returns Array of validators
   **/
  public get zipControl(): any[] {
    return [
      '',
      []];
  }

  /**
   * @function countryControl Controls for country field
   * @returns Array of validators
   **/
  public get countryControl(): any[] {
    return [
      { value: '', disabled: true },
      []];
  }

  /**
 * @function signupTypeControl Controls for country field
 * @returns Array of validators
 **/
  public get signupTypeControl(): any[] {
    return [
      '',
      []];
  }

  /**
* @function getSignupType retrieves the signup type image
*/
  public getSignupType(type: string): string {
    switch (type) {
      case 'facebook':
        return '/icons/facebook.png';
      case 'google':
        return '/icons/google.png';
      case 'apple':
        return '/icons/apple.png';
      default:
        return '';

    }
  }
  /**
   * @function getCreateSource gets the translation of the create source
   */
  public getCreateSource(createSource: string): string {
    switch (createSource) {
      case 'CT_FR':
      case '1A':
        return 'customer.create-type.CT_FR';
      case 'US_SELF_AZURE':
      case 'TC_AGENT':
        return 'customer.create-type.' + createSource;
      default:
        return 'customer.create-type.unknown';
    }
  }
}

/**
  * @class Contains the properties available of the phone number information
  */
export class PhoneNumber {
  countryCode: number;
  @InputConverter()
  phoneVerified: boolean;
  phoneNumber: string;
  phoneType: string;
}

/**
  * @class Contains the properties available of the Email information
  */
export class Email {
  emailAddress: string;
  emailType: string;
  @InputConverter()
  emailVerified: boolean;
}

/**
  * @class Contains the properties available of the Customer address information
  */
export class CustomerAddress {
  addressType: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
  address: string;
}

export class AddressTranslation {
  private usCountry = {
    city: 'city',
    state: 'state',
    zip: 'zip',
  };
  private caCountry = {
    city: 'city',
    state: 'province',
    zip: 'postal',
  };
  public country: { city: string; state: string; zip: string };

  constructor(private translator: TranslationService) {
    this.translator = translator;
    this.country = this.usCountry;
  }

  public getCountry(countryCode: string): AddressTranslation {
    if (countryCode === 'CA') {
      this.country = this.caCountry;
    } else if (countryCode === 'US' || countryCode === 'MX') {
      this.country = this.usCountry;
    }
    return this;
  }

  public requireValidation(value: string) {
    let translationString: string;
    switch (value) {
      case 'city':
        translationString = 'address.validations.city-required';
        break;
      case 'state':
        translationString = 'address.validations.state-required';
        break;
      case 'province':
        translationString = 'address.validations.province-required';
        break;
      case 'zip':
        translationString = 'address.validations.zip-required';
        break;
      case 'postal':
        translationString = 'address.validations.postal-required';
        break;
    }
    return this.translator.getCustomTranslation(translationString)['value'];
  }

  public patternValidation(value: string) {
    let translationString: string;
    switch (value) {
      case 'city':
        translationString = 'address.validations.city-pattern';
        break;
      case 'state':
        translationString = 'address.validations.state-pattern';
        break;
      case 'province':
        translationString = 'address.validations.province-pattern';
        break;
      case 'zip':
        translationString = 'address.validations.zip-pattern';
        break;
      case 'postal':
        translationString = 'address.validations.postal-pattern';
        break;
    }
    return this.translator.getCustomTranslation(translationString)['value'];
  }

  public formLabel(value: string) {
    let translationString: string;
    switch (value) {
      case 'city':
        translationString = 'address.form.city';
        break;
      case 'state':
        translationString = 'address.form.state';
        break;
      case 'province':
        translationString = 'address.form.province';
        break;
      case 'zip':
        translationString = 'address.form.zip';
        break;
      case 'postal':
        translationString = 'address.form.postal-code';
        break;
    }
    return this.translator.getCustomTranslation(translationString)['value'];
  }
}
